/**
 * Pack authoring assist — PACK_AUTHORING conversation in workspace pane.
 * Planning-chat standard: prose in chat, pack form updates on the right panel automatically.
 */

import React, { useMemo } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { ConversationShell } from '@/components/ai-studio/conversation';
import type { ActiveConversationContext } from '@/types/aiStudio';
import type { PackAuthoringPatch } from '@/types/aiStudioAgent';
import {
  ASSIST_PANE_HEADER_CLASS,
  ASSIST_PANE_TITLE_CLASS,
} from '@/components/ai-studio/assist/assistPaneStyles';
import { usePackAuthoring } from './usePackAuthoring';

const PACK_HINTS = [
  'Injection molding vocabulary: press, mold, resin',
  'Append inputs for CSV upload columns',
  'Resource type for injection presses with tonnage',
];

interface PackAuthoringInterviewProps {
  packId: string;
  packName: string;
  enabled: boolean;
  workspacePane?: boolean;
  onApplyPatch: (patch: PackAuthoringPatch) => void;
}

export const PackAuthoringInterview: React.FC<PackAuthoringInterviewProps> = ({
  packId,
  packName,
  enabled,
  workspacePane = false,
  onApplyPatch,
}) => {
  const { t } = useLanguage();
  const authoring = usePackAuthoring(packId, enabled, onApplyPatch);

  const context: ActiveConversationContext = useMemo(
    () => ({
      contextType: 'PACK_AUTHORING',
      scopeLabel: packName,
      status: 'open',
    }),
    [packName]
  );

  if (!enabled) return null;

  const chatShell = authoring.loading ? (
    <p className="px-3 py-2 text-base text-[var(--color-surface-text-secondary)]">
      {t('aiStudio.packs.loading')}
    </p>
  ) : (
    <ConversationShell
      className="min-h-0 flex-1"
      context={context}
      messages={authoring.messages}
      sending={authoring.sending}
      error={authoring.errorKey ? t(authoring.errorKey) : null}
      onSend={(text) => void authoring.send(text)}
      hideContextIndicator
      paneMode={workspacePane}
      composerVariant="pill"
      composerFloating
      composerHints={PACK_HINTS}
      composerHintStepMs={5000}
      composerPlaceholder={t('aiStudio.packAuthoring.placeholder')}
    />
  );

  if (workspacePane) {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        <header className={ASSIST_PANE_HEADER_CLASS}>
          <h2 className={ASSIST_PANE_TITLE_CLASS}>{t('aiStudio.packAuthoring.title')}</h2>
        </header>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[var(--color-secondary-bg)]">
          {chatShell}
        </div>
      </div>
    );
  }

  return (
    <section className="flex h-full min-h-0 flex-col gap-2 overflow-hidden">
      <header className="flex-shrink-0 px-1">
        <h2 className="text-base font-semibold text-[var(--color-surface-text)]">
          {t('aiStudio.packAuthoring.title')}
        </h2>
        <p className="mt-0.5 text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.packAuthoring.subtitle')}
        </p>
      </header>
      {chatShell}
    </section>
  );
};
